import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, RefreshCw } from 'lucide-react';

/**
 * Balance Display Component with hide/show toggle and refresh
 * 
 * Features:
 * - Toggle balance visibility
 * - Refresh button with animation
 * - Persists preference in localStorage
 * - Shows asterisks when hidden
 * - Customizable styling
 * - Auto-refresh indicator
 */
const BalanceDisplay = ({ 
  balance = 0,
  currency = "USDT",
  symbol = "",
  size = "medium",
  className = "",
  showToggle = true,
  showRefresh = true,
  onRefresh = null,
  isRefreshing = false,
  defaultVisible = true,
  storageKey = "balance_visibility",
  lastUpdated = null
}) => {
  // Get initial visibility from localStorage or use default
  const [isVisible, setIsVisible] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored !== null ? stored === 'true' : defaultVisible;
  });
  const [refreshing, setRefreshing] = useState(false);

  // Update localStorage when visibility changes
  useEffect(() => {
    localStorage.setItem(storageKey, isVisible.toString());
  }, [isVisible, storageKey]);

  const toggleVisibility = () => {
    setIsVisible(!isVisible);
  };

  const handleRefresh = async () => {
    if (onRefresh && !refreshing && !isRefreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  // Format balance with currency
  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Size classes
  const sizeClasses = {
    small: 'text-lg',
    medium: 'text-2xl',
    large: 'text-3xl sm:text-4xl',
    xlarge: 'text-4xl sm:text-5xl'
  };

  const isSpinning = refreshing || isRefreshing;

  return (
    <div className={`flex items-center gap-2 sm:gap-3 ${className}`}>
      <div className={`font-bold ${sizeClasses[size] || sizeClasses.medium}`}>
        {isVisible ? (
          <>
            {symbol && <span className="text-gray-400">{symbol}</span>}
            <span className={isSpinning ? 'opacity-50' : ''}>{formatBalance(balance)}</span>
            {currency && (
              <span className="text-sm ml-1 sm:ml-2 text-gray-400">{currency}</span>
            )}
          </>
        ) : (
          <span className="text-gray-500">••••••</span>
        )}
      </div>
      
      <div className="flex items-center gap-1">
        {showRefresh && onRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isSpinning}
            className={`p-1.5 sm:p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 ${isSpinning ? 'cursor-not-allowed' : ''}`}
            aria-label="Refresh balance"
            title="Refresh balance"
          >
            <RefreshCw className={`h-4 w-4 sm:h-5 sm:w-5 ${isSpinning ? 'animate-spin' : ''}`} />
          </button>
        )}
        
        {showToggle && (
          <button
            onClick={toggleVisibility}
            className="p-1.5 sm:p-2 rounded-lg hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
            aria-label={isVisible ? "Hide balance" : "Show balance"}
            title={isVisible ? "Hide balance" : "Show balance"}
          >
            {isVisible ? (
              <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Compact Balance Card with refresh
 * For use in headers and summary sections
 */
export const BalanceCard = ({
  title = "Available Balance",
  balance = 0,
  currency = "USDT",
  onRefresh = null,
  isRefreshing = false,
  className = "",
  variant = "default" // default, success, warning
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (onRefresh && !refreshing && !isRefreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const variantStyles = {
    default: 'bg-gray-800/50 border-gray-700',
    success: 'bg-green-500/10 border-green-500/30',
    warning: 'bg-yellow-500/10 border-yellow-500/30'
  };

  const isSpinning = refreshing || isRefreshing;

  return (
    <div className={`rounded-xl p-3 sm:p-4 border ${variantStyles[variant]} ${className}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs sm:text-sm text-gray-400">{title}</span>
        {onRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isSpinning}
            className={`p-1 rounded hover:bg-gray-700/50 text-gray-400 hover:text-white transition-colors ${isSpinning ? 'cursor-not-allowed' : ''}`}
            title="Refresh"
          >
            <RefreshCw className={`h-3 w-3 sm:h-4 sm:w-4 ${isSpinning ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>
      <div className={`text-xl sm:text-2xl font-bold text-white ${isSpinning ? 'opacity-50' : ''}`}>
        ${formatBalance(balance)} <span className="text-sm text-gray-400">{currency}</span>
      </div>
    </div>
  );
};

/**
 * Simple balance text display with optional hiding and refresh
 * For inline use in sentences or smaller displays
 */
export const BalanceText = ({ 
  balance = 0,
  currency = "USDT",
  symbol = "$",
  className = "",
  showRefresh = false,
  onRefresh = null,
  isRefreshing = false,
  hideByDefault = false
}) => {
  const [isVisible, setIsVisible] = useState(!hideByDefault);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (onRefresh && !refreshing && !isRefreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isSpinning = refreshing || isRefreshing;

  return (
    <span className={`inline-flex items-center gap-1 sm:gap-2 ${className}`}>
      {isVisible ? (
        <span className={isSpinning ? 'opacity-50' : ''}>
          {symbol}{formatBalance(balance)} {currency}
        </span>
      ) : (
        <span className="text-gray-500">••••••</span>
      )}
      
      {showRefresh && onRefresh && (
        <button
          onClick={handleRefresh}
          disabled={isSpinning}
          className={`inline-flex p-1 rounded hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors ${isSpinning ? 'cursor-not-allowed' : ''}`}
          title="Refresh"
        >
          <RefreshCw className={`h-3 w-3 ${isSpinning ? 'animate-spin' : ''}`} />
        </button>
      )}
      
      <button
        onClick={() => setIsVisible(!isVisible)}
        className="inline-flex p-1 rounded hover:bg-gray-800/50 text-gray-400 hover:text-white transition-colors"
        aria-label={isVisible ? "Hide amount" : "Show amount"}
      >
        {isVisible ? (
          <EyeOff className="h-3 w-3 sm:h-4 sm:w-4" />
        ) : (
          <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
        )}
      </button>
    </span>
  );
};

/**
 * Real-time synced balance display
 * Shows sync status and last updated time
 */
export const SyncedBalanceDisplay = ({
  balance = 0,
  currency = "USDT",
  onRefresh = null,
  isRefreshing = false,
  loading = false,
  lastUpdated = null,
  className = ""
}) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    if (onRefresh && !refreshing && !isRefreshing) {
      setRefreshing(true);
      try {
        await onRefresh();
      } finally {
        setTimeout(() => setRefreshing(false), 500);
      }
    }
  };

  const formatBalance = (amount) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const isSpinning = refreshing || isRefreshing || loading;

  return (
    <div className={`flex flex-col ${className}`}>
      <div className="flex items-center gap-2">
        <span className={`text-2xl sm:text-3xl font-bold text-white ${isSpinning ? 'opacity-50' : ''}`}>
          ${formatBalance(balance)}
        </span>
        <span className="text-sm text-gray-400">{currency}</span>
        {onRefresh && (
          <button
            onClick={handleRefresh}
            disabled={isSpinning}
            className={`p-1.5 rounded-lg hover:bg-gray-700/50 text-gray-400 hover:text-green-400 transition-colors ${isSpinning ? 'cursor-not-allowed' : ''}`}
            title="Refresh balance"
          >
            <RefreshCw className={`h-4 w-4 ${isSpinning ? 'animate-spin text-green-400' : ''}`} />
          </button>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1">
        <span className={`w-2 h-2 rounded-full ${isSpinning ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`}></span>
        <span className="text-xs text-gray-500">
          {isSpinning ? 'Syncing...' : 'Real-time sync'}
        </span>
      </div>
    </div>
  );
};

export default BalanceDisplay;
